 /**
  * AdminRouteGuard - Route Guard for Internal Admin Routes
  * 
  * Enforces admin role requirement for /admin/* routes.
  * Shows 403 Forbidden for non-admin users attempting access.
  */
 
 import { ReactNode } from 'react';
 import { Navigate, useLocation, Link } from 'react-router-dom';
 import { useAuth } from '@/contexts/AuthContext';
 import { Loader2, ShieldX, Home, ArrowLeft } from 'lucide-react';
 import { Button } from '@/components/ui/button';
 import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
 
 /**
  * Loading spinner component for auth checks
  */
 function AuthLoader() {
   return (
     <div className="min-h-screen flex items-center justify-center bg-background">
       <Loader2 className="h-8 w-8 animate-spin text-primary" />
     </div>
   );
 }
 
 /**
  * 403 Forbidden component for unauthorized deep-link access attempts
  */
 function ForbiddenAccess({ attemptedPath }: { attemptedPath: string }) {
   return (
     <div className="min-h-screen flex items-center justify-center bg-background p-4">
       <Card className="max-w-md w-full">
         <CardHeader className="text-center pb-2">
           <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
             <ShieldX className="h-8 w-8 text-destructive" />
           </div>
           <CardTitle className="text-2xl">403 Forbidden</CardTitle>
           <CardDescription className="text-base">
             You don't have permission to access this page
           </CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
           <p className="text-sm text-muted-foreground text-center">
             The resource at <code className="px-1 py-0.5 bg-muted rounded text-xs">{attemptedPath}</code> requires admin privileges.
           </p>
           
           <div className="flex flex-col sm:flex-row gap-3 pt-4">
             <Button
               variant="outline"
               onClick={() => window.history.back()}
               className="flex-1"
             >
               <ArrowLeft className="h-4 w-4 mr-2" />
               Go Back
             </Button>
             <Button asChild className="flex-1">
               <Link to="/">
                 <Home className="h-4 w-4 mr-2" />
                 Home
               </Link>
             </Button>
           </div>
           
           <p className="text-xs text-muted-foreground text-center pt-4">
             If you believe this is an error, please contact support.
           </p>
         </CardContent>
       </Card>
     </div>
   );
 }
 
 interface AdminRouteProps {
   children: ReactNode;
 }
 
 /**
  * AdminRoute - Route guard for admin-only pages
  * 
  * Guards /admin/* routes. Requires user with admin role.
  * Shows 403 Forbidden for non-admin users.
  */
 export function AdminRoute({ children }: AdminRouteProps) {
   const { user, isAdmin, isLoading, isRoleLoading } = useAuth();
   const location = useLocation();
 
   // Show loader while auth OR role check is being determined
   // This prevents the flash of 403 on hard refresh
   if (isLoading || isRoleLoading) {
     return <AuthLoader />;
   }
 
   // Not logged in - redirect to admin login with next parameter
   if (!user) {
     const next = encodeURIComponent(location.pathname + location.search);
     return <Navigate to={`/admin/login?next=${next}`} replace />;
   }
 
   // Must be admin - all other personas get 403 Forbidden
   if (!isAdmin) {
     return <ForbiddenAccess attemptedPath={location.pathname} />;
   }
 
   return <>{children}</>;
 }